import React from "react";
import { useCC, ccClient, scopeParams } from "../lib/ccClient";
import { Panel, AsyncPanelBody, HealthBadge, EvidenceBadge, ModuleStatusBadge, EmptyState } from "../components/primitives";
import { formatMetric } from "../lib/evidence";
import { resolveItemStatus } from "../lib/registry";
import { CheckCircle2, ExternalLink } from "lucide-react";
import { toast } from "sonner";

export function ConnectorsPanel({ scope, range }) {
  const params = scopeParams(scope, range);
  const { loading, error, data, refetch } = useCC("/connectors", params);
  const rows = data?.connectors || [];
  return (
    <Panel testid="cc-panel-connectors" title="Connector Hub / Supplier Integration"
      subtitle="Provider-neutral integration health — real observed states only">
      <AsyncPanelBody loading={loading} error={error} onRetry={refetch} empty={rows.length === 0 && { title: "No connectors registered" }}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-zinc-500 border-b border-white/10">
                <th className="py-2 pr-4">Integration</th><th className="py-2 pr-4">State</th>
                <th className="py-2 pr-4">Observed</th><th className="py-2">Source</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.key} data-testid={`cc-connector-${r.key}`} className="border-b border-white/5">
                  <td className="py-3 pr-4 text-white font-medium">{r.label}</td>
                  <td className="py-3 pr-4"><HealthBadge state={r.state} /></td>
                  <td className="py-3 pr-4 text-zinc-400 text-xs">{r.observedAt || "—"}</td>
                  <td className="py-3 text-zinc-500 text-xs uppercase tracking-wider">{r.source}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </AsyncPanelBody>
    </Panel>
  );
}

export function SystemHealthPanel({ scope, range }) {
  const params = scopeParams(scope, range);
  const { loading, error, data, refetch } = useCC("/system-health", params);
  const services = data?.services || [];
  return (
    <Panel testid="cc-panel-system-health" title="System Health"
      subtitle="Service-level state — UNKNOWN where not instrumented (never assumed HEALTHY)">
      <AsyncPanelBody loading={loading} error={error} onRetry={refetch}>
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
          {services.map((s) => (
            <div key={s.key} data-testid={`cc-service-${s.key}`}
              className="bg-[#141a28] border border-white/10 rounded-lg p-3 flex flex-col gap-2">
              <span className="text-[11px] font-bold text-white capitalize">{s.key.replace(/_/g, " ")}</span>
              <HealthBadge state={s.state} />
              <span className="text-[9px] text-zinc-500">{s.observedAt || "not observed"}</span>
            </div>
          ))}
        </div>
      </AsyncPanelBody>
    </Panel>
  );
}

export function AlertsPanel({ scope, range, canAck }) {
  const params = scopeParams(scope, range);
  const { loading, error, data, refetch } = useCC("/alerts", params);
  const alerts = data?.alerts || [];
  const ack = async (id) => {
    try {
      await ccClient().post(`/alerts/${id}/acknowledge`);
      toast.success("Alert acknowledged");
      refetch();
    } catch (e) {
      toast.error(e?.response?.data?.error?.message || "Could not acknowledge");
    }
  };
  return (
    <Panel testid="cc-panel-alerts" title="Alert Center"
      subtitle="Append-only alert store — an empty feed is truthfully empty, not HEALTHY">
      <AsyncPanelBody loading={loading} error={error} onRetry={refetch}
        empty={alerts.length === 0 && { title: "No recorded alerts", message: "No alert events have been recorded for this scope." }}>
        <ul className="divide-y divide-white/5">
          {alerts.map((a) => (
            <li key={a.alert_id || a.id} data-testid="cc-alert-row" className="py-3 flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2">
                  <HealthBadge state={(a.severity || "UNKNOWN").toUpperCase()} />
                  <span className="text-sm font-bold text-white">{a.title || a.type}</span>
                </div>
                <p className="text-xs text-zinc-400 mt-1">{a.source_authority} · {a.created_at}</p>
              </div>
              {canAck && !a.acknowledged_at && (
                <button onClick={() => ack(a.alert_id)} data-testid="cc-alert-ack"
                  className="inline-flex items-center gap-1 text-xs font-bold text-black bg-[#C5A059] px-2.5 py-1 rounded-md hover:bg-white">
                  <CheckCircle2 className="w-3.5 h-3.5" /> Ack
                </button>
              )}
            </li>
          ))}
        </ul>
      </AsyncPanelBody>
    </Panel>
  );
}

export function ApprovalsPanel({ scope, range }) {
  const params = scopeParams(scope, range);
  const { loading, error, data, refetch } = useCC("/approvals", params);
  const cats = data?.categories || {};
  const labels = {
    sellerVerification: "Seller Verification", productApproval: "Product Approval",
    highRiskOrderReview: "High-Risk Orders", refundExceptions: "Refund Exceptions",
    payoutExceptions: "Payout Exceptions", connectorAuthorization: "Connector Authorization",
    advertisingApproval: "Advertising Approval", marketplaceConfig: "Marketplace Config",
  };
  return (
    <Panel testid="cc-panel-approvals" title="Approval Center"
      subtitle="Pending approvals by category — dispatch is capability-gated & workflow-truthful">
      <AsyncPanelBody loading={loading} error={error} onRetry={refetch}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Object.entries(labels).map(([k, label]) => {
            const f = formatMetric(cats[k]);
            return (
              <div key={k} data-testid={`cc-approval-${k}`} className="bg-[#141a28] border border-white/10 rounded-lg p-4">
                <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">{label}</p>
                <p className={`text-2xl font-black mt-1 ${f.isValue ? "text-white" : "text-zinc-500"}`}>
                  {f.isValue ? f.display : <span className="text-xs">{f.display}</span>}
                </p>
                <div className="mt-2"><EvidenceBadge state={f.state} /></div>
              </div>
            );
          })}
        </div>
        {data?.queueDepth && (
          <p className="text-xs text-zinc-400 mt-4">Queue depth: <span className="text-white font-bold">{formatMetric(data.queueDepth).display}</span></p>
        )}
      </AsyncPanelBody>
    </Panel>
  );
}

export function ActivityPanel({ scope, range }) {
  const params = scopeParams(scope, range);
  const { loading, error, data, refetch } = useCC("/activity", params);
  const items = data?.activity || [];
  return (
    <Panel testid="cc-panel-activity" title="Activity Feed" subtitle="Immutable audit timeline">
      <AsyncPanelBody loading={loading} error={error} onRetry={refetch}
        empty={items.length === 0 && { title: "No activity recorded" }}>
        <ul className="divide-y divide-white/5">
          {items.map((a, i) => (
            <li key={a.activityId || i} data-testid="cc-activity-row" className="py-2.5 flex items-center justify-between gap-4">
              <div className="min-w-0">
                <p className="text-sm text-white font-medium truncate">{a.action}</p>
                <p className="text-xs text-zinc-500 truncate">{a.actor || a.actorId || "system"}{a.entityId ? ` · ${a.entityId}` : ""}</p>
              </div>
              <span className="text-[10px] text-zinc-500 whitespace-nowrap">{a.occurredAt}</span>
            </li>
          ))}
        </ul>
      </AsyncPanelBody>
    </Panel>
  );
}

export function OmadaPanel({ scope, range }) {
  const params = scopeParams(scope, range);
  const { loading, error, data, refetch } = useCC("/omada", params);
  const decision = data?.decision || "UNKNOWN";
  // Never infer GO. Any non-explicit-GO decision renders as pending independent approval.
  const isGo = decision === "GO";
  const decisionLabel = isGo ? "GO" : decision === "NO-GO" ? "NO-GO" : "PENDING INDEPENDENT APPROVAL";
  const decisionChip = isGo ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/30"
    : decision === "NO-GO" ? "bg-red-500/10 text-red-300 border-red-500/30"
    : "bg-yellow-500/10 text-yellow-300 border-yellow-500/30";
  return (
    <Panel testid="cc-panel-omada" title="OMADA Release Status"
      subtitle="Read-only governance projection — no override authority">
      <AsyncPanelBody loading={loading} error={error} onRetry={refetch}>
        <div className="flex flex-wrap items-center gap-3 mb-5">
          <span className={`inline-flex items-center px-4 py-2 rounded-lg border text-sm font-black uppercase tracking-wider ${decisionChip}`}
            data-testid="cc-omada-decision">
            {decisionLabel}
          </span>
          <span className="text-xs text-zinc-500">Read-only · issued by independent OMADA/OAEQA</span>
        </div>
        <dl className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <Row k="Release Candidate" v={data?.releaseCandidateId} />
          <Row k="Parent RC" v={data?.parentReleaseCandidateId} />
          <Row k="Source Manifest SHA-256" v={data?.sourceManifestSha256} mono />
          <Row k="Evidence Bundle SHA-256" v={data?.evidenceBundleSha256} mono />
        </dl>
        <div className="mt-5">
          <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 mb-2">Open Findings</p>
          {(data?.openFindings || []).length === 0 ? (
            <p className="text-xs text-zinc-500">No open findings reported in the evidence bundle.</p>
          ) : (
            <ul className="space-y-1.5">
              {data.openFindings.map((f, i) => (
                <li key={i} className="text-xs text-zinc-300 bg-white/5 border border-white/10 rounded-md px-3 py-2">
                  {typeof f === "string" ? f : JSON.stringify(f)}
                </li>
              ))}
            </ul>
          )}
        </div>
      </AsyncPanelBody>
    </Panel>
  );
}

const Row = ({ k, v, mono }) => (
  <div className="bg-[#141a28] border border-white/10 rounded-lg px-4 py-3">
    <dt className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">{k}</dt>
    <dd className={`text-white mt-1 break-all ${mono ? "font-mono text-xs" : "text-sm font-medium"}`}>{v || "—"}</dd>
  </div>
);

export function AegisPanel({ scope, range }) {
  const params = scopeParams(scope, range);
  const { loading, error, data, refetch } = useCC("/aegis", params);
  const runtime = data?.runtime || [];
  const deps = data?.dependencies || [];
  return (
    <Panel testid="cc-panel-aegis" title="AEGIS Digital Twin — Executive Summary"
      subtitle="Verified runtime signals only — uninstrumented evidence shows UNKNOWN / NOT_CONNECTED"
      actions={<span data-testid="cc-aegis-twinstate"><ModuleStatusBadge status={data?.twinState || "NOT_CONNECTED"} /></span>}>
      <AsyncPanelBody loading={loading} error={error} onRetry={refetch}>
        <div className="bg-[#141a28] border border-white/10 rounded-lg px-4 py-3 mb-5 text-xs text-zinc-400" data-testid="cc-aegis-note">
          {data?.note}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 mb-2">Runtime Health</p>
            <div className="grid grid-cols-2 gap-2">
              {runtime.map((s) => (
                <div key={s.key} className="bg-[#141a28] border border-white/10 rounded-lg p-3 flex flex-col gap-1.5">
                  <span className="text-[11px] font-bold text-white capitalize">{s.key.replace(/_/g, " ")}</span>
                  <HealthBadge state={s.state} />
                  <span className="text-[9px] text-zinc-500">{s.observedAt || "not observed"}</span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 mb-2">Dependencies</p>
            <div className="space-y-2">
              {deps.map((d) => (
                <div key={d.key} className="flex items-center justify-between bg-[#141a28] border border-white/10 rounded-lg px-3 py-2">
                  <span className="text-xs text-white">{d.label}</span>
                  <HealthBadge state={d.state} />
                </div>
              ))}
            </div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 mt-4 mb-2">Incidents</p>
            <div className="bg-[#141a28] border border-white/10 rounded-lg p-4">
              <p className="text-2xl font-black text-white">{formatMetric(data?.incidents?.open).display}</p>
              <p className="text-[10px] text-zinc-500 mt-1">{data?.incidents?.note}</p>
            </div>
            <p className="text-[10px] text-zinc-500 mt-3" data-testid="cc-aegis-freshness">
              Evidence freshness: {data?.evidenceFreshness?.state} · asOf {data?.evidenceFreshness?.asOf}
            </p>
          </div>
        </div>
      </AsyncPanelBody>
    </Panel>
  );
}

export function LiveVersePanel({ scope, range }) {
  const params = scopeParams(scope, range);
  const { loading, error, data, refetch } = useCC("/liveverse", params);
  const cards = [
    { k: "liveNow", label: "Live Now" }, { k: "scheduled", label: "Scheduled" },
    { k: "totalSessions", label: "Total Sessions" }, { k: "activeViewers", label: "Active Viewers" },
  ];
  return (
    <Panel testid="cc-panel-liveverse" title="LiveVerse — Executive Summary"
      subtitle="Real live_sessions read model — counts VERIFIED; uninstrumented health shows UNKNOWN"
      actions={data?._connectivity && <EvidenceBadge state={data._connectivity.state} />}>
      <AsyncPanelBody loading={loading} error={error} onRetry={refetch}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {cards.map(({ k, label }) => {
            const f = formatMetric(data?.[k]);
            return (
              <div key={k} data-testid={`cc-liveverse-${k}`} className="bg-[#141a28] border border-white/10 rounded-lg p-4">
                <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">{label}</p>
                <p className={`text-2xl font-black mt-1 ${f.isValue ? "text-white" : "text-zinc-500"}`}>
                  {f.isValue ? f.display : <span className="text-xs">{f.display}</span>}
                </p>
                <div className="mt-2"><EvidenceBadge state={f.state} /></div>
              </div>
            );
          })}
        </div>
        <div className="grid grid-cols-2 gap-3 mt-3">
          <div className="flex items-center justify-between bg-[#141a28] border border-white/10 rounded-lg px-4 py-3">
            <span className="text-xs text-white">Stream Health</span><HealthBadge state={data?.streamHealth?.state || "UNKNOWN"} />
          </div>
          <div className="flex items-center justify-between bg-[#141a28] border border-white/10 rounded-lg px-4 py-3">
            <span className="text-xs text-white">Moderation Health</span><HealthBadge state={data?.moderationHealth?.state || "UNKNOWN"} />
          </div>
        </div>
      </AsyncPanelBody>
    </Panel>
  );
}

// Future-module contract surface: shows the truthful registration status + safe deep-links.
export function ContractPanel({ item, flags, adminPerms, onOpenAdmin }) {
  const status = resolveItemStatus(item, flags, adminPerms);
  const messages = {
    FEATURE_DISABLED: "This module is registered but its feature flag is disabled. No runtime data is available.",
    NOT_IMPLEMENTED: "This module is reserved via a route contract and is not yet implemented. No engine is mounted.",
    NOT_CONNECTED: "This surface has no connected data source yet. It will populate once its integration is connected.",
    ACCESS_RESTRICTED: "Your account does not have the capability required to view this module.",
    ACTIVE: "This module is active.",
  };
  return (
    <Panel testid={`cc-contract-${item.id}`} title={item.label}
      actions={<ModuleStatusBadge status={status} />}>
      <EmptyState title={item.label} message={messages[status]} />
    </Panel>
  );
}

// Admin deep-link surface: opens the protected existing /admin section (no duplicate engine).
export function AdminDeepLink({ item, adminPerms, onOpenAdmin }) {
  const restricted = item.perm && adminPerms && !adminPerms.has(item.perm);
  return (
    <Panel testid={`cc-deeplink-${item.id}`} title={item.label}
      actions={<ModuleStatusBadge status={restricted ? "ACCESS_RESTRICTED" : "ACTIVE"} />}>
      {restricted ? (
        <EmptyState title="Access Restricted" message={`Requires the "${item.perm}" capability.`} />
      ) : (
        <div className="flex flex-col items-start gap-4 py-6">
          <p className="text-sm text-zinc-300 max-w-lg">
            This capability runs on the protected <span className="text-[#C5A059] font-bold">/admin</span> operational surface.
            Open it directly — all existing functionality is preserved.
          </p>
          <button onClick={() => onOpenAdmin(item.section)} data-testid={`cc-open-admin-${item.id}`}
            className="inline-flex items-center gap-2 bg-[#C5A059] text-black px-4 py-2.5 rounded-lg text-sm font-bold hover:bg-white transition-colors">
            <ExternalLink className="w-4 h-4" /> Open {item.label}
          </button>
        </div>
      )}
    </Panel>
  );
}
